import { HTMLAttributes, useState } from 'react'

import Container from '@/components/base/Container'
import Grid from '@/components/base/Grid'
import Section from '@/components/base/Section'
import Typography from '@/components/base/Typography'

import { cn } from '@/lib/utils'

export type Accordion = {
  title: string
  paragraph: string
}

export type AccordionProps = HTMLAttributes<HTMLElement> & Partial<Accordion>

const Accordion = ({
  title,
  paragraph,
  className,
  ...props
}: AccordionProps) => {
  const [active, setActive] = useState(false)

  return (
    <div
      className={cn([
        'border-b border-mainbody/20 py-[2.4rem]',
        'group-[.modal]:border-dark-blue/20',
        className
      ])}
      {...props}
    >
      <button
        className="flex h-auto w-full items-center space-x-[10rem] text-left"
        onClick={() => setActive((state) => !state)}
      >
        <Typography variant="h5" component="h5" className="grow">
          {title}
        </Typography>

        <span
          className={cn([
            'relative flex h-[1.2rem] w-[1.2rem] shrink-0',
            'before:absolute before:left-1/2 before:top-1/2 before:h-[.2rem] before:w-[1.2rem] before:-translate-x-1/2 before:-translate-y-1/2 before:rounded-full before:bg-mainbody group-[.modal]:before:bg-dark-blue',
            'after:absolute after:left-1/2 after:top-1/2 after:h-[.2rem] after:w-[1.2rem] after:-translate-x-1/2 after:-translate-y-1/2 after:rotate-90 after:rounded-full after:bg-mainbody group-[.modal]:after:bg-dark-blue',
            'before:transition-transform before:duration-300 before:ease-in-out-cubic',
            'after:transition-transform after:duration-300 after:ease-in-out-cubic',
            {
              'before:rotate-180 after:rotate-180': active
            }
          ])}
        />
      </button>
      <div
        className={cn([
          'grid overflow-hidden',
          'transition-all duration-300 ease-in-out-cubic',
          {
            'grid-rows-[0fr] opacity-0': !active,
            'grid-rows-[1fr] opacity-100': active
          }
        ])}
      >
        <div className="overflow-hidden">
          <Typography variant="body" paragraph className="mt-[2.4rem]">
            {paragraph}
          </Typography>
        </div>
      </div>
    </div>
  )
}

export type AccordionsProps = HTMLAttributes<HTMLElement> &
  Partial<{
    collection: Accordion[]
  }>

const Accordions = ({ collection, className, ...props }: AccordionsProps) => {
  return (
    <Section className={cn([className])} {...props}>
      <Container>
        <Grid>
          {collection?.map((accordion, index) => (
            <Accordion
              key={`accordion-${index}`}
              className={cn([
                'col-span-4 md:col-span-4 md:col-start-2 lg:col-span-6 lg:col-start-4',
                'md:group-[.modal]:col-span-6 lg:group-[.modal]:col-span-12'
              ])}
              {...accordion}
            />
          ))}
        </Grid>
      </Container>
    </Section>
  )
}

export default Accordions
